'use client'

import { RIIPFormDialog } from '@/components/forms/riip-form-dialog'
import {
  PremiumFeature,
  PremiumHeading,
  PremiumHero,
  PremiumIntro,
  PremiumSection,
} from '@/components/premium/premium-ui'
import { Button } from '@/components/ui/button'
import {
  Award,
  Backpack,
  BookOpen,
  Flag,
  GraduationCap,
  Heart,
  Mail,
  Map,
  Medal,
  Palette,
  PenLine,
  School,
  Sparkles,
  Star,
  Target,
  Users,
  Video,
} from 'lucide-react'
import Image from 'next/image'
import { toast } from 'sonner'

const studentActivities = [
  ['Pack your backpack', Backpack, 'Pick the experiences you are most excited about in your own state or district.'],
  ['Write to the host', Mail, 'Share why you want to visit and build a meaningful connection before the trip.'],
  ['Plan the itinerary', Map, 'Use the workbook to budget, plan and prepare for the perfect day.'],
  ['Colour a saree', Palette, 'Design your pattern and receive it back as a keepsake.'],
  ['Byte video', Video, 'Create a short video about the host and experience.'],
  ['Poster presentation', PenLine, 'Present the cultural asset like a showcase expert.'],
] as const

const schoolBenefits = [
  ['Enrich learning', GraduationCap, 'Bring textbook topics to life through real-world experiences.'],
  ['Build values', Users, 'Encourage respect for traditions, empathy and community pride.'],
  ['Curriculum alignment', Target, 'Support experiential learning across multiple curricular goals.'],
  ['Recognition', Award, 'Schools can earn recognition through active participation.'],
  ['Community connection', Star, 'Create lasting relationships with local artisans and heritage communities.'],
] as const

export function CAPPage() {
  const handleSuccess = () => {
    toast.success('CAP inquiry submitted', { description: 'Our team will contact you with next steps.' })
  }
  const handleError = () => {
    toast.error('Submission failed', { description: 'Please try again or contact support.' })
  }

  return (
    <div className="bg-background">
      <PremiumHero
        image="/riip-image.jpeg"
        imageAlt="Students on an Oorva cultural ambassador field visit with local hosts"
        eyebrow="CAP"
        title={
          <>
            Cultural Ambassador
            <br />
            Programme for Schools
          </>
        }
        subtitle="Explore. Experience. Express. Inspire. A hands-on cultural learning journey for students in their own district."
      >
        <RIIPFormDialog
          dialogTitle="CAP Programme Inquiry"
          submitLabel="Talk to our CAP team"
          inquiryType="cap"
          trigger={<Button className="min-h-12 rounded-full bg-brand px-7 text-white hover:bg-brand-hover">Bring CAP to your school</Button>}
          onSuccess={handleSuccess}
          onError={handleError}
        />
      </PremiumHero>

      <PremiumSection>
        <div className="grid gap-8 rounded-[1.5rem] border border-border bg-card p-6 md:grid-cols-[0.9fr_1.1fr] md:p-10">
          <div>
            <PremiumHeading className="text-3xl md:text-5xl">What is CAP?</PremiumHeading>
            <PremiumIntro className="mt-5">
              Oorva&apos;s Cultural Ambassador Programme is a 4-6 week journey where students
              explore, experience and celebrate the living culture of their own district.
            </PremiumIntro>
            <PremiumIntro className="mt-4">
              At the heart of CAP is one full experience day where students meet a practitioner,
              try the tradition, document the story and create memories that last a lifetime.
            </PremiumIntro>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <PremiumFeature icon={<Users className="h-5 w-5" />} title="Experiential learning" body="Students learn by doing, asking and making." />
              <PremiumFeature icon={<Heart className="h-5 w-5" />} title="Local connection" body="Real people, real stories and real community pride." />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="relative min-h-[340px] overflow-hidden rounded-2xl sm:row-span-2">
              <Image src="/weekend-trips/betel-farm-walk.webp" alt="Students learning from a local host" fill className="object-cover" sizes="(min-width: 768px) 28vw, 100vw" />
            </div>
            <div className="relative min-h-[160px] overflow-hidden rounded-2xl">
              <Image src="/weekend-trips/doll-making-painting.png" alt="Hands-on craft activity" fill className="object-cover" sizes="(min-width: 768px) 28vw, 100vw" />
            </div>
            <div className="relative min-h-[160px] overflow-hidden rounded-2xl">
              <Image src="/sirumugai-story.jpeg" alt="Students documenting cultural stories" fill className="object-cover" sizes="(min-width: 768px) 28vw, 100vw" />
            </div>
          </div>
        </div>
      </PremiumSection>

      <PremiumSection>
        <div className="mx-auto max-w-3xl text-center">
          <PremiumHeading className="text-3xl md:text-5xl">What students actually do</PremiumHeading>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-3 lg:grid-cols-6">
          {studentActivities.map(([title, Icon, body]) => (
            <PremiumFeature key={title} icon={<Icon className="h-5 w-5" />} title={title} body={body} />
          ))}
        </div>
      </PremiumSection>

      <PremiumSection className="pt-0">
        <div>
          <div className="mx-auto max-w-3xl text-center">
            <PremiumHeading className="text-3xl md:text-5xl">Become a certified Cultural Ambassador</PremiumHeading>
            <PremiumIntro className="mt-4">
              Every student receives a CAP passport, badge and certificate as a proud Cultural
              Ambassador.
            </PremiumIntro>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            <PremiumFeature icon={<BookOpen className="h-5 w-5" />} title="Passport" body="A guided workbook to plan, observe and reflect." />
            <PremiumFeature icon={<Medal className="h-5 w-5" />} title="Badge" body="A symbol of participation and cultural curiosity." />
            <PremiumFeature icon={<Award className="h-5 w-5" />} title="Certificate" body="Recognition for completing the ambassador journey." />
          </div>
        </div>
      </PremiumSection>

      <PremiumSection>
        <div className="mx-auto max-w-3xl text-center">
          <PremiumHeading className="text-3xl md:text-5xl">Why schools will love CAP</PremiumHeading>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-5">
          {schoolBenefits.map(([title, Icon, body]) => (
            <PremiumFeature key={title} icon={<Icon className="h-5 w-5" />} title={title} body={body} />
          ))}
        </div>
      </PremiumSection>

      <PremiumSection className="pt-0">
        <div className="grid gap-6 md:grid-cols-2">
          <JourneyCard
            title="Grade 3-5"
            subtitle="Discover, imagine and experience."
            image="/weekend-trips/doll-making-painting.png"
            points={['Fun activities, stories and creative tasks', 'Hands-on experiences and simple planning', 'Encourages observation, curiosity and expression']}
          />
          <JourneyCard
            title="Grade 6-9"
            subtitle="Explore deeper, think bigger."
            image="/experiences/karuppur-kalamkari-dyes.jpg"
            points={['Critical thinking and problem-solving', 'Planning, budgeting and storytelling', 'Digital creativity and communication']}
          />
        </div>
      </PremiumSection>

      <PremiumSection className="pt-0">
        <div className="rounded-[1.5rem] border border-border bg-card p-6 md:p-10">
          <PremiumHeading className="text-3xl md:text-4xl">Planning CAP with your school</PremiumHeading>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            <p className="text-sm leading-7 text-muted-foreground"><strong className="text-foreground">Who it is for.</strong> The activities shown here are designed for Grades 3–9. Tell us the grades and expected student count so we can discuss a suitable format.</p>
            <p className="text-sm leading-7 text-muted-foreground"><strong className="text-foreground">Typical sequence.</strong> Classroom preparation, one hosted experience day, then student documentation and a presentation. The final schedule and location are agreed with the school.</p>
            <p className="text-sm leading-7 text-muted-foreground"><strong className="text-foreground">Logistics and supervision.</strong> We will confirm the meeting point, transport arrangements, accessibility needs, host activity and adult supervision responsibilities with your team before the visit.</p>
            <p className="text-sm leading-7 text-muted-foreground"><strong className="text-foreground">Pricing and next steps.</strong> Send an enquiry for a programme quote and proposed plan. The cost, group size and response timing are confirmed directly with your school before you commit.</p>
          </div>
        </div>
      </PremiumSection>

      <PremiumSection tone="forest">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <Flag className="mb-5 h-12 w-12 text-brand-highlight" />
            <PremiumHeading className="text-white">
              Bring CAP to
              <br />
              <span className="italic text-brand-highlight">your school.</span>
            </PremiumHeading>
            <p className="mt-5 max-w-md text-white/68">
              Let your students explore, experience, express and become the next generation of
              Cultural Ambassadors.
            </p>
          </div>
          <div className="rounded-3xl border border-white/10 bg-white/[0.055] p-8">
            <School className="mb-5 h-10 w-10 text-brand-highlight" />
            <h3 className="font-sans text-4xl font-semibold tracking-[-0.035em] text-white">We would love to connect.</h3>
            <p className="mt-3 text-white/62">Share your institution details and our CAP team will reach out.</p>
            <div className="mt-7">
              <RIIPFormDialog
                dialogTitle="CAP Programme Inquiry"
                submitLabel="Talk to our CAP team"
                inquiryType="cap"
                trigger={<Button className="min-h-12 rounded-full bg-brand px-7 text-white hover:bg-brand-hover">Talk to our CAP team</Button>}
                onSuccess={handleSuccess}
                onError={handleError}
              />
            </div>
          </div>
        </div>
      </PremiumSection>
    </div>
  )
}

function JourneyCard({
  title,
  subtitle,
  points,
  image,
}: {
  title: string
  subtitle: string
  points: string[]
  image: string
}) {
  return (
    <div className="grid gap-5 rounded-[1.5rem] border border-border bg-card p-5 sm:grid-cols-[1fr_0.9fr]">
      <div>
        <h3 className="font-sans text-3xl font-semibold tracking-[-0.025em]">{title}</h3>
        <p className="mt-1 text-sm font-semibold text-muted-foreground">{subtitle}</p>
        <ul className="mt-5 space-y-3">
          {points.map((point) => (
            <li key={point} className="flex gap-2 text-sm leading-6 text-muted-foreground">
              <Sparkles className="mt-1 h-4 w-4 shrink-0 text-brand" />
              {point}
            </li>
          ))}
        </ul>
      </div>
      <div className="relative min-h-52 overflow-hidden rounded-xl">
        <Image src={image} alt={title} fill className="object-cover" sizes="(min-width: 640px) 22vw, 100vw" />
      </div>
    </div>
  )
}
